/**
 * Validación de coordenadas y lógica de distancia confiable para empresas.
 * Solo se calcula y muestra distancia cuando las coordenadas son válidas.
 */

/** Latitud válida: numérico en [-90, 90], no 0 si se quiere evitar punto en el mar como "sin dato" */
function isValidLat(value) {
  if (value == null || value === '' || value === undefined) return false;
  const n = Number(value);
  if (Number.isNaN(n)) return false;
  if (n < -90 || n > 90) return false;
  return true;
}

/** Longitud válida: numérico en [-180, 180] */
function isValidLng(value) {
  if (value == null || value === '' || value === undefined) return false;
  const n = Number(value);
  if (Number.isNaN(n)) return false;
  if (n < -180 || n > 180) return false;
  return true;
}

/** Par (0, 0) suele usarse como "sin dato"; no considerarlo confiable para Chile */
const COORD_NULL_ISLAND_LAT = 0;
const COORD_NULL_ISLAND_LNG = 0;

/**
 * Indica si un valor numérico es una coordenada válida (lat o lng según tipo).
 * @param {*} value
 * @param {'lat'|'lng'} type
 */
export function isValidCoordinate(value, type) {
  if (type === 'lat') return isValidLat(value);
  if (type === 'lng') return isValidLng(value);
  return false;
}

/**
 * True si la entidad tiene lat y lng válidos (numéricos, en rango, no null island).
 * @param {{ latitud?: *, longitud?: *, ubicacion?: { lat?: *, lng?: * }, coordenadas?: { lat?: *, lng?: * } }} entity
 */
export function hasValidLatLng(entity) {
  if (!entity || typeof entity !== 'object') return false;
  const lat = entity.latitud ?? entity.ubicacion?.lat ?? entity.coordenadas?.lat;
  const lng = entity.longitud ?? entity.ubicacion?.lng ?? entity.coordenadas?.lng;
  if (!isValidLat(lat) || !isValidLng(lng)) return false;
  const latN = Number(lat);
  const lngN = Number(lng);
  if (latN === COORD_NULL_ISLAND_LAT && lngN === COORD_NULL_ISLAND_LNG) return false;
  return true;
}

/**
 * Extrae { lat, lng } solo si son válidos; si no, null.
 */
export function getCompanyCoords(entity) {
  if (!hasValidLatLng(entity)) return null;
  const lat = entity.latitud ?? entity.ubicacion?.lat ?? entity.coordenadas?.lat;
  const lng = entity.longitud ?? entity.ubicacion?.lng ?? entity.coordenadas?.lng;
  return { lat: Number(lat), lng: Number(lng) };
}

/**
 * True si la referencia de ubicación (usuario o Santiago) tiene coordenadas válidas.
 */
export function isRefLocationValid(ref) {
  if (!ref || typeof ref !== 'object') return false;
  const lat = ref.lat ?? ref.latitud;
  const lng = ref.lng ?? ref.longitud;
  return isValidLat(lat) && isValidLng(lng);
}

/**
 * Normaliza texto de región/comuna para comparación (lowercase, sin acentos extra, trim).
 */
function normalizeRegionText(str) {
  if (typeof str !== 'string' || !str.trim()) return '';
  return str
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * True si ambas regiones parecen la misma (p. ej. "Metropolitana" vs "Región Metropolitana").
 */
export function isSameRegion(regionA, regionB) {
  const a = normalizeRegionText(regionA || '');
  const b = normalizeRegionText(regionB || '');
  if (!a || !b) return false;
  if (a === b) return true;
  const aNorm = a.replace(/^region\s*/i, '').replace(/\s*de\s*santiago/i, '');
  const bNorm = b.replace(/^region\s*/i, '').replace(/\s*de\s*santiago/i, '');
  if (aNorm === bNorm) return true;
  if (a.includes('metropolitana') && b.includes('metropolitana')) return true;
  return false;
}

/**
 * Indica si la empresa tiene al menos comuna o región informada (para no mostrar distancia falsa).
 */
export function hasRegionOrComuna(entity) {
  if (!entity) return false;
  const comuna = (entity.comuna ?? entity.ciudad ?? '').trim();
  const region = (entity.region ?? entity.region_empresa ?? '').trim();
  return comuna.length > 0 || region.length > 0;
}

/**
 * Calcula distancia en km (Haversine) solo si ambas ubicaciones tienen coords válidos.
 * Retorna null si alguna no es confiable.
 * @param {Object} company - Empresa con latitud/longitud o ubicacion
 * @param {{ lat: number, lng: number }} refLocation - Referencia (usuario o Santiago)
 * @param {function} calculateDistanceKm - (lat1, lng1, lat2, lng2) => number
 */
/**
 * Distancia Haversine en km sin redondear. Usar para ordenar; para mostrar en UI usar getReliableDistanceKm.
 */
export function getDistanceKmUnrounded(company, refLocation, calculateDistanceKm) {
  if (!calculateDistanceKm || !isRefLocationValid(refLocation)) return null;
  const coords = getCompanyCoords(company);
  if (!coords) return null;
  const latRef = Number(refLocation.lat ?? refLocation.latitud);
  const lngRef = Number(refLocation.lng ?? refLocation.longitud);
  const km = calculateDistanceKm(latRef, lngRef, coords.lat, coords.lng);
  if (km == null || Number.isNaN(km) || km < 0) return null;
  return km;
}

export function getReliableDistanceKm(company, refLocation, calculateDistanceKm) {
  const km = getDistanceKmUnrounded(company, refLocation, calculateDistanceKm);
  if (km == null) return null;
  return Math.round(km * 10) / 10;
}

/**
 * Texto de ubicación breve: comuna, o comuna + región, o región sola.
 * No incluye "Ubicación no especificada"; devuelve '' si no hay nada útil.
 */
function getLocationFallbackText(entity) {
  if (!entity || typeof entity !== 'object') return '';
  const comuna = (entity.comuna ?? entity.ciudad ?? '').trim();
  let region = (entity.region ?? entity.region_empresa ?? '').trim();
  if (comuna && region) {
    const regionDisplay = /^regi[oó]n\s+/i.test(region) ? region : `Región ${region}`;
    return `${comuna}, ${regionDisplay}`;
  }
  if (comuna) return comuna;
  if (region) return /^regi[oó]n\s+/i.test(region) ? region : `Región ${region}`;
  const direccion = (entity.direccion ?? entity.direccion_empresa ?? '').trim();
  if (direccion) {
    const firstPart = direccion.split(',')[0].trim();
    return firstPart.length > 50 ? firstPart.slice(0, 47) + '…' : firstPart;
  }
  return '';
}

/**
 * Devuelve el dato a mostrar en ficha de empresa: distancia confiable o fallback textual.
 * Solo muestra distancia si la referencia del usuario es válida (ubicación real), no fallback Santiago.
 * @param {Object} company - Empresa (latitud, longitud, ubicacion, comuna, region, direccion, etc.)
 * @param {{ lat, lng }|null} userLocation - Ubicación del usuario (null = no mostrar km)
 * @param {function} calculateDistanceKm - (lat1, lng1, lat2, lng2) => number
 * @returns {{ display: string, isDistance: boolean } | null} - null si no hay nada que mostrar
 */
export function getCompanyLocationDisplay(company, userLocation, calculateDistanceKm) {
  if (!company || typeof company !== 'object') return null;

  // 1. Distancia en km solo si el usuario tiene ubicación válida (no Santiago por defecto)
  if (userLocation && isRefLocationValid(userLocation) && calculateDistanceKm) {
    const km = getReliableDistanceKm(company, userLocation, calculateDistanceKm);
    if (km != null && !Number.isNaN(km)) {
      return { display: `A ${Number(km).toFixed(1)} km de ti`, isDistance: true };
    }
  }

  // 2. Fallback: comuna, comuna+región o dirección breve
  const fallback = getLocationFallbackText(company);
  if (fallback) return { display: fallback, isDistance: false };

  return null;
}
